import { isPlatformBrowser } from '@angular/common';
import { Component, OnInit, OnDestroy, ElementRef, Renderer2, ViewChildren, ViewChild, QueryList, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { MenuModel } from '@models/menuModel';
import { MenuService } from '@services/menu.service';
import { categoryPath } from '@services/seo-url.util';
import { safeLocalStorage } from '@shared/safe-web-storage';

import * as $ from 'jquery'

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.css'],
  standalone: false
}) 
export class MenuComponent implements OnInit, OnDestroy, AfterViewInit {
  public lsMenu: any;
  public MenuFinal: Array<MenuModel> = [];
  private routerEventsSubscription!: Subscription;
  private submenuChangesSubscription!: Subscription;
  private removeDocumentClickListener?: () => void;
  private menuLoaded = false;

  // Set para manejar múltiples submenús abiertos de forma independiente
  public openMobileSubmenuIds: Set<number> = new Set();

  @ViewChildren('submenu') submenus!: QueryList<ElementRef>;
  @ViewChild('navbarCollapse') navbarCollapse?: ElementRef<HTMLElement>;
  @ViewChild('navbarToggler') navbarToggler?: ElementRef<HTMLButtonElement>;

  constructor(
    private menuService: MenuService,
    private router: Router,
    private renderer: Renderer2,
    private el: ElementRef,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnInit() {
    if (this.isBrowser) {
      this.removeDocumentClickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (!this.el.nativeElement.contains(target)) {
          const submenus = this.el.nativeElement.querySelectorAll('.navLastchild');
          submenus.forEach((submenu: HTMLElement) => {
            this.renderer.removeClass(submenu, 'open-to-left');
          });
        }
      });
    }
    
    if (!this.menuLoaded) {
      this.loadMenu();
      this.initializeBootNavbar();
      this.menuLoaded = true;
    }

    this.routerEventsSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateActiveMenuItems();
        // Cerrar todos los submenús móviles al navegar
        this.closeAllMobileSubmenus();
        this.closeMobileNavbar();
      });
  }

  ngAfterViewInit() {
    if (!this.isBrowser) return;

    this.submenuChangesSubscription = this.submenus.changes.subscribe(() => {
      setTimeout(() => {
        this.adjustSubmenuPositions();
        this.initializeMobileMenuBehavior();
      }, 0);
    });
    
    // Inicializar comportamiento mobile después de la primera carga
    setTimeout(() => {
      this.initializeMobileMenuBehavior();
    }, 100);
  }

  ngOnDestroy() {
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
    if (this.submenuChangesSubscription) {
      this.submenuChangesSubscription.unsubscribe();
    }
    if (this.removeDocumentClickListener) {
      this.removeDocumentClickListener();
    }
  }

  private initializeBootNavbar() {
    if (!this.isBrowser) return;

    $.getScript('/Public/Js/bootnavbar.js');
  }

  private updateActiveMenuItems() {
    const currentUrl = this.router.url;
    // Lógica para actualizar clases activas
  }

  private async loadMenu() {
    try {
      this.MenuFinal = [];
      this.lsMenu = await this.menuService.GetMenu(-1);
      this.generaMenu(this.lsMenu);
      if (this.isBrowser) safeLocalStorage.setItem('Menu', JSON.stringify(this.MenuFinal));
      
      if (this.isBrowser) {
        setTimeout(() => {
          this.adjustSubmenuPositions();
          this.initializeMobileMenuBehavior();
        }, 0);
      }
    } catch (error) {
      console.error('Error al cargar el menú:', error);
    }
  }

  private generaMenu(menu: any) {
    for (const item of menu) {
      if (item.IdCategoriaPadre === 0) {
        const menutemp = new MenuModel();
        menutemp.IdCategoria = item.IdCat;
        menutemp.IdCategoriaPadre = item.IdCategoriaPadre;
        menutemp.Categoria = item.Categoria;
        menutemp.url = item.url;
        menutemp.Hijos = this.agregaHijo(menu, item.IdCat,
          menu.filter((m: MenuModel) => m.IdCategoriaPadre === item.IdCat));
        this.MenuFinal.push(menutemp);
      }
    }
  }

  private agregaHijo(menu: any, padreId: number, hijos: any[]): Array<MenuModel> {
    const menuT: Array<MenuModel> = [];
    for (const item of hijos) {
      if (item.IdCategoriaPadre === padreId) {
        const menutemp = new MenuModel();
        menutemp.IdCategoria = item.IdCat;
        menutemp.IdCategoriaPadre = item.IdCategoriaPadre;
        menutemp.Categoria = item.Categoria;
        menutemp.url = item.url;
        menutemp.Hijos = this.agregaHijo(menu, item.IdCat,
          menu.filter((m: MenuModel) => m.IdCategoriaPadre === item.IdCat));
        menuT.push(menutemp);
      }
    }
    return menuT;
  }

  private adjustSubmenuPositions() {
    if (!this.isBrowser) return;

    const submenus = this.el.nativeElement.querySelectorAll('.navLastchild');
    
    submenus.forEach((submenu: HTMLElement) => {
      this.renderer.removeClass(submenu, 'open-to-left');
      
      void submenu.offsetWidth;
      
      const rect = submenu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const parentRect = submenu.parentElement?.getBoundingClientRect();
      
      const isNestedSubmenu = submenu.parentElement?.parentElement?.classList.contains('navLastchild');
      
      if ((rect.right > viewportWidth) || 
          (isNestedSubmenu && parentRect && (parentRect.right + 200 > viewportWidth))) {
        this.renderer.addClass(submenu, 'open-to-left');
      }
    });
  }

  /**
   * Inicializa el comportamiento mobile:
   * - Cierra todos los submenús al inicio
   * - Asegura que solo el hover funcione en desktop
   */
  private initializeMobileMenuBehavior() {
    if (!this.isBrowser) return;

    if (window.innerWidth <= 1199) {
      // Cerrar todos los submenús en mobile
      const allSubmenus = this.el.nativeElement.querySelectorAll('.navLastchild');
      allSubmenus.forEach((submenu: HTMLElement) => {
        this.renderer.setStyle(submenu, 'display', 'none');
        this.renderer.removeClass(submenu, 'mobile-open');
      });
      
      // Limpiar el Set
      this.openMobileSubmenuIds.clear();
    }
  }

  /**
   * Cierra todos los submenús móviles
   */
  private closeAllMobileSubmenus() {
    if (!this.isBrowser) return;

    this.openMobileSubmenuIds.clear();
    
    if (window.innerWidth <= 1199) {
      const allSubmenus = this.el.nativeElement.querySelectorAll('.navLastchild');
      allSubmenus.forEach((submenu: HTMLElement) => {
        this.renderer.setStyle(submenu, 'display', 'none');
        this.renderer.removeClass(submenu, 'mobile-open');
      });
    }
  }

  /**
   * Cierra el contenedor mobile abierto desde el toggle de Bootstrap
   */
  private closeMobileNavbar() {
    if (!this.isBrowser || window.innerWidth > 1199) return;

    const collapseElement = this.navbarCollapse?.nativeElement
      ?? this.el.nativeElement.querySelector('#navbarSupportedContent');
    const togglerElement = this.navbarToggler?.nativeElement
      ?? this.el.nativeElement.querySelector('.navbar-toggler');

    if (collapseElement) {
      this.renderer.removeClass(collapseElement, 'collapsing');
      this.renderer.addClass(collapseElement, 'collapse');
      this.renderer.removeClass(collapseElement, 'show');
      this.renderer.setStyle(collapseElement, 'height', '');
      this.renderer.setStyle(collapseElement, 'display', '');
    }

    if (togglerElement) {
      this.renderer.setAttribute(togglerElement, 'aria-expanded', 'false');
      this.renderer.addClass(togglerElement, 'collapsed');
    }
  }

  /**
   * Cierra los hermanos del submenu que se esta abriendo
   */
  private closeSiblingSubmenus(categoryId: number) {
    // Buscar el elemento del menú actual
    const currentMenu = this.findMenuById(this.MenuFinal, categoryId);
    if (!currentMenu) return;

    // Buscar el padre
    const parent = this.findParentMenu(this.MenuFinal, categoryId);
    
    if (parent) {
      // Cerrar todos los hermanos
      parent.Hijos.forEach((hermano: MenuModel) => {
        if (hermano.IdCategoria !== categoryId) {
          this.closeMenuAndChildren(hermano.IdCategoria);
        }
      });
    } else {
      // Si no hay padre, son de primer nivel, cerrar los otros de primer nivel
      this.MenuFinal.forEach((menu: MenuModel) => {
        if (menu.IdCategoria !== categoryId) {
          this.closeMenuAndChildren(menu.IdCategoria);
        }
      });
    }
  }

  /**
   * Cierra un menú y todos sus hijos recursivamente
   */
  private closeMenuAndChildren(categoryId: number) {
    // Remover del Set
    this.openMobileSubmenuIds.delete(categoryId);
    
    // Buscar el menú y cerrar sus hijos
    const menu = this.findMenuById(this.MenuFinal, categoryId);
    if (menu && menu.Hijos.length > 0) {
      menu.Hijos.forEach((hijo: MenuModel) => {
        this.closeMenuAndChildren(hijo.IdCategoria);
      });
    }
    
    // Actualizar visualmente el submenú
    const submenuElement = this.isBrowser ? this.el.nativeElement.querySelector(`#nav${categoryId}`) : null;
    if (submenuElement) {
      this.renderer.setStyle(submenuElement, 'display', 'none');
      this.renderer.removeClass(submenuElement, 'mobile-open');
    }
  }

  /**
   * Busca un menú por ID en toda la estructura
   */
  private findMenuById(menus: MenuModel[], id: number): MenuModel | null {
    for (const menu of menus) {
      if (menu.IdCategoria === id) {
        return menu;
      }
      if (menu.Hijos.length > 0) {
        const found = this.findMenuById(menu.Hijos, id);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Busca el padre de un menú
   */
  private findParentMenu(menus: MenuModel[], childId: number): MenuModel | null {
    for (const menu of menus) {
      if (menu.Hijos.some(hijo => hijo.IdCategoria === childId)) {
        return menu;
      }
      if (menu.Hijos.length > 0) {
        const found = this.findParentMenu(menu.Hijos, childId);
        if (found) return found;
      }
    }
    return null;
  }

  public onMenuItemHover(event: MouseEvent, menuItem: any) {
    if (!this.isBrowser) return;

    // Solo funciona en desktop
    if (window.innerWidth > 1199) {
      const submenu = (event.target as HTMLElement).querySelector('.navLastchild');
      
      if (submenu) {
        setTimeout(() => this.adjustSubmenuPositions(), 0);
    
        submenu.addEventListener('mouseleave', () => {
          this.renderer.removeClass(submenu, 'open-to-left');
        });
      }
    }
  }

  public VentanaProductos(_Cat: any, _event: MouseEvent) {
    this.closeAllMobileSubmenus();
    this.closeMobileNavbar();
  }

  public categoryPath(Cat: any): string {
    return categoryPath(Cat);
  }

  /**
   * Toggle para submenús móviles con cierre de hermanos
   */
  public toggleMobileSubmenu(Cat: any, event: MouseEvent) {
    if (!this.isBrowser) return;

    event.preventDefault();
    event.stopPropagation();
    
    // Solo funciona en mobile
    if (window.innerWidth > 1199) return;
    
    const isCurrentlyOpen = this.openMobileSubmenuIds.has(Cat.IdCategoria);
    
    if (isCurrentlyOpen) {
      // Cerrar este menú y todos sus hijos
      this.closeMenuAndChildren(Cat.IdCategoria);
    } else {
      // Cerrar los hermanos
      this.closeSiblingSubmenus(Cat.IdCategoria);
      
      // Abrir este menú
      this.openMobileSubmenuIds.add(Cat.IdCategoria);
      
      // Actualizar visualmente
      const submenuElement = this.el.nativeElement.querySelector(`#nav${Cat.IdCategoria}`);
      if (submenuElement) {
        this.renderer.setStyle(submenuElement, 'display', 'block');
        this.renderer.addClass(submenuElement, 'mobile-open');
      }
    }
  }

  public isMobileSubmenuOpen(categoryId: number): boolean {
    return this.openMobileSubmenuIds.has(categoryId);
  }

  private get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
